import { useState, useEffect, useRef, useCallback } from 'react'
import { Head, router } from '@inertiajs/react'
import { Team, MetricWithEntries, MetricCategory, DateColumn, FilterParams, User, CheckInData } from '../../types'
import Layout from '../../components/Layout'
import ScoreboardFilters, { GROUP_OPTIONS } from '../../components/scoreboard/ScoreboardFilters'
import ScoreboardTable from '../../components/scoreboard/ScoreboardTable'
import AddMetricModal from '../../components/scoreboard/AddMetricModal'
import EditMetricModal from '../../components/scoreboard/EditMetricModal'
import EditCategoriesModal from '../../components/scoreboard/EditCategoriesModal'
import EditViewModal, { ViewSettings } from '../../components/scoreboard/EditViewModal'
import EntryNotesModal, { EntryDetailUpdate } from '../../components/scoreboard/EntryNotesModal'
import { computeOnTarget, computeWon } from '../../components/scoreboard/winRules'
import { formatTargetDisplay } from '../../components/scoreboard/formatters'
import { scoreCellKey } from '../../components/scoreboard/MetricRow'
import { useSaveQueue } from '../../components/scoreboard/useSaveQueue'

// The independently-writable parts of a score cell. Saves, confirmed state
// and rollback are all tracked per field, because one request can persist
// Won/Lost while a separate request for the same cell rejects its value.
type CellField = 'value' | 'won'

interface Props {
  team: Team
  active_tab: string
  metrics: MetricWithEntries[]
  categories: MetricCategory[]
  date_columns: DateColumn[]
  team_members: User[]
  filters: FilterParams
  can_reorder_metric?: boolean
}

const SAVE_DEBOUNCE_MS = 800

export default function Scoreboard({ team, active_tab, metrics, categories, date_columns, team_members, filters, can_reorder_metric }: Props) {
  const [groupBy, setGroupBy] = useState('none')
  const [viewMode, setViewMode] = useState<'numbers' | 'wonlost' | 'both'>('both')
  const [currentWeekVisible, setCurrentWeekVisible] = useState(true)
  // Presentation only: keep the complete columns for reconciliation and notes.
  // Plain local state survives prop refreshes without persisting across reloads.
  const visibleDateColumns = date_columns.filter(col =>
    filters.cadence !== 'weekly' || currentWeekVisible || !col.is_current
  )
  // Local mirror of the server's metric rows. Cells are edited inline, so a
  // background prop refresh must not clobber an in-flight edit — the mirror
  // only re-syncs when the set of metrics or a metric's DEFINITION changes
  // (add/edit/delete/archive, filter change, different date columns).
  const [orderedMetrics, setOrderedMetrics] = useState<MetricWithEntries[]>(metrics)

  const metricsKey = date_columns.map(c => c.key).join(',') + '|' + metrics.map(m => [
    m.id, m.name, m.description ?? '', m.owner?.id ?? '', m.target ?? '', m.format ?? '',
    m.decimal_places ?? '', m.abbreviate_large_numbers ?? '', m.goal_direction ?? '',
    m.comparison ?? '', m.frequency ?? '', m.category_id ?? '', m.state ?? '', m.position,
  ].join('|')).join(',')
  const prevMetricsKeyRef = useRef(metricsKey)
  useEffect(() => {
    if (prevMetricsKeyRef.current !== metricsKey) {
      prevMetricsKeyRef.current = metricsKey
      setOrderedMetrics(metrics)
    }
  }, [metricsKey, metrics])

  // Every write on this page (cell values, Won/Lost, notes, row reorder)
  // funnels through one serialized queue so Inertia can't cancel an
  // in-flight save when the next one starts.
  const { enqueue, dispose } = useSaveQueue()

  // Per-cell saving state, keyed `${metricId}:${columnKey}` so one cell's
  // indicator never disables its siblings.
  const [savingCells, setSavingCells] = useState<Set<string>>(new Set())
  // Per-cell debounce timers, so typing in one cell cannot cancel another
  // cell's pending save.
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  // Latest pending save per cell so Enter can flush it immediately. The
  // entry is deleted before running, so a later debounce tick (or a second
  // Enter) can't double-save the same edit.
  const pendingSaves = useRef<Record<string, () => void>>({})
  // Last server-confirmed state per cell — seeded from props the first time
  // a cell is touched and advanced on every successful save, so a failure
  // reverts to what the server actually holds rather than to the value the
  // cell happened to start the session with.
  const confirmedEntries = useRef<Record<string, CheckInData | undefined>>({})
  // Monotonic edit counter per cell FIELD. A save carries the revisions it
  // was enqueued at and may only roll a field back if it is still the latest
  // write to that field — so a late failure can't stomp a newer edit, and a
  // failed value write is still reverted even though a Won/Lost write for
  // the same cell came after it.
  const fieldRevisions = useRef<Record<string, Record<CellField, number>>>({})
  // Refs to each editable cell input, keyed `${metricId}:${columnKey}`, so
  // Enter can move focus to the same column on the next displayed row.
  const valueInputRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const [saveError, setSaveError] = useState<string | null>(null)
  // Metrics whose inline Target edit (task #650) is in flight.
  const [savingTargets, setSavingTargets] = useState<Set<number>>(new Set())
  // Monotonic edit counter per metric target, so a late failure can only roll
  // back the edit it was carrying — never a newer one typed since.
  const targetRevisions = useRef<Record<number, number>>({})
  // Outstanding target writes per metric: with two in flight, the first one
  // settling must not clear the indicator the second still needs.
  const targetSaveCounts = useRef<Record<number, number>>({})
  // The last target the SERVER confirmed — the only valid rollback baseline
  // when consecutive edits fail.
  const confirmedTargets = useRef<Record<number, number | null>>({})

  const [addMetricOpen, setAddMetricOpen] = useState(false)
  const [editMetric, setEditMetric] = useState<MetricWithEntries | null>(null)
  const [notesMetricId, setNotesMetricId] = useState<number | null>(null)
  const [categoriesOpen, setCategoriesOpen] = useState(false)
  const [viewSettingsOpen, setViewSettingsOpen] = useState(false)
  const [viewSettings, setViewSettings] = useState<ViewSettings>({
    showYtd: true,
    showAverage: false,
    showTeam: false,
    showCategory: false,
    highlightCurrentWeek: true,
  })

  const handleReorder = (sourceIndex: number, destinationIndex: number) => {
    const next = Array.from(orderedMetrics)
    const [moved] = next.splice(sourceIndex, 1)
    next.splice(destinationIndex, 0, moved)
    setOrderedMetrics(next)

    // Through the same queue as the cell saves so a reorder can't cancel a
    // debounced value edit that is still in flight.
    const positions = next.map((m, i) => ({ id: m.id, position: i }))
    enqueue({
      key: 'metrics:reorder',
      url: `/teams/${team.id}/scoreboard/metrics/reorder`,
      body: { positions },
      onError: (detail) => setSaveError(`The new row order could not be saved${detail ? `: ${detail}` : '.'}`),
    })
  }

  // The metrics in the exact top-to-bottom order ScoreboardTable renders
  // them (mirrors its grouping logic), so Enter walks the column in
  // displayed order without skipping rows.
  const displayedOrder = (() => {
    if (groupBy === 'category') {
      const grouped = categories.flatMap(cat => orderedMetrics.filter(m => m.category_id === cat.id))
      return [...grouped, ...orderedMetrics.filter(m => !m.category_id)]
    }
    if (groupBy === 'owner') {
      const ownerMap: Record<string, MetricWithEntries[]> = {}
      const keys: string[] = []
      orderedMetrics.forEach(m => {
        const key = m.owner ? `${m.owner.id}` : 'unassigned'
        if (!ownerMap[key]) {
          ownerMap[key] = []
          keys.push(key)
        }
        ownerMap[key].push(m)
      })
      return keys.flatMap(k => ownerMap[k])
    }
    return orderedMetrics
  })()

  const bumpRevisions = useCallback((key: string, fields: CellField[]) => {
    const revs = { ...(fieldRevisions.current[key] ?? { value: 0, won: 0 }) }
    fields.forEach(f => { revs[f] = revs[f] + 1 })
    fieldRevisions.current[key] = revs
    return revs
  }, [])

  // A cell can have more than one operation outstanding (a value save still
  // in flight while its replacement waits in the queue), so this counts them
  // rather than flipping a flag. Clearing on the first completion would
  // re-enable the Won/Lost toggle while a write to the cell is still open.
  const savingCounts = useRef<Record<string, number>>({})
  const markSaving = useCallback((key: string, on: boolean) => {
    const count = Math.max(0, (savingCounts.current[key] ?? 0) + (on ? 1 : -1))
    savingCounts.current[key] = count
    setSavingCells(prev => {
      if ((count > 0) === prev.has(key)) return prev
      const next = new Set(prev)
      if (count > 0) next.add(key); else next.delete(key)
      return next
    })
  }, [])

  // YTD mirrors the server compute (sum of entries recorded on/after Jan 1
  // of the current year). Editing a period only shifts YTD when that
  // period falls in the same year as the current column.
  // The backend sums entries whose OWN recorded_at falls in the current
  // calendar year, so the optimistic delta has to use the same two facts —
  // not the column's start date, which can sit in the previous year for a
  // week that straddles New Year.
  const currentYear = String(new Date().getFullYear())
  const applyYtdDelta = useCallback((metric: MetricWithEntries, column: DateColumn, oldValue: number | null, newValue: number | null): number => {
    const recordedAt = metric.entries[column.key]?.recorded_at || column.start_date
    if (recordedAt.slice(0, 4) !== currentYear) return metric.ytd_total ?? 0
    return (metric.ytd_total ?? 0) - (oldValue ?? 0) + (newValue ?? 0)
  }, [currentYear])

  // Applies a patch to one cell of one metric in the local mirror, keeping
  // every other row and cell untouched.
  const patchEntry = useCallback((
    metricId: number,
    column: DateColumn,
    build: (metric: MetricWithEntries, entry: CheckInData | undefined) => { entry: CheckInData | undefined; ytd?: number }
  ) => {
    setOrderedMetrics(prev => prev.map(m => {
      if (m.id !== metricId) return m
      const existing = m.entries[column.key]
      const { entry, ytd } = build(m, existing)
      const entries = { ...m.entries }
      if (entry) entries[column.key] = entry
      else delete entries[column.key]
      return { ...m, entries, ytd_total: ytd ?? m.ytd_total }
    }))
  }, [])

  // Rolls the FAILED FIELDS of one cell back to their last server-confirmed
  // state, leaving fields the server did accept (and every other cell)
  // alone. YTD moves by this cell's own delta rather than being restored
  // wholesale, so edits to other periods in the same row survive.
  const revertFields = useCallback((metricId: number, column: DateColumn, key: string, fields: CellField[]) => {
    if (!(key in confirmedEntries.current)) return
    const confirmed = confirmedEntries.current[key]
    patchEntry(metricId, column, (m, displayed) => {
      if (!displayed) {
        return { entry: confirmed, ytd: applyYtdDelta(m, column, null, confirmed?.value ?? null) }
      }
      // Rolling every field back to "no entry at all" removes the cell.
      if (!confirmed && fields.includes('value') && fields.includes('won')) {
        return { entry: undefined, ytd: applyYtdDelta(m, column, displayed.value ?? null, null) }
      }
      const next: CheckInData = { ...displayed }
      let ytd = m.ytd_total ?? 0
      if (fields.includes('value')) {
        const target = confirmed?.value ?? null
        ytd = applyYtdDelta(m, column, displayed.value ?? null, target)
        next.value = target
        next.on_track = computeOnTarget(target, m.target, m.goal_direction, m.comparison)
      }
      if (fields.includes('won')) {
        next.won_the_week_manual = confirmed?.won_the_week_manual ?? false
        next.won_the_week = confirmed?.won_the_week ?? null
      }
      // Won/Lost follows the value ONLY while nobody has overridden it. An
      // override the user has set but this request never carried is a live
      // field of its own — rolling the value back must leave it alone.
      if (!next.won_the_week_manual) {
        next.won_the_week = computeWon(next.value, m.target, m.goal_direction, m.comparison)
      }
      return { entry: next, ytd }
    })
  }, [patchEntry, applyYtdDelta])

  // Every inline write goes through the team metric-entry CREATE endpoint —
  // it upserts by recorded date, so a brand-new cell needs no entry id. But
  // the date it upserts on must be the EXISTING entry's recorded_at when
  // there is one: the serializer buckets entries into period columns by a
  // normalized key while exposing the raw date, and those differ (e.g.
  // after the team's week-start day changes). Posting the column's start
  // date in that case inserts a SECOND entry for the same period instead of
  // updating it — inflating YTD and stranding the original's notes.
  const recordedAtFor = useCallback((metric: MetricWithEntries, column: DateColumn) => (
    metric.entries[column.key]?.recorded_at || column.start_date
  ), [])

  const entriesUrl = useCallback((metricId: number) => (
    // Carry the scoreboard's query string (cadence / weeks / state / search)
    // so the save's redirect keeps the current view instead of dropping the
    // URL back to the weekly default.
    `/teams/${team.id}/scoreboard/metrics/${metricId}/entries${typeof window !== 'undefined' ? window.location.search : ''}`
  ), [team.id])

  // All saves go through one serialized queue — see useSaveQueue: Inertia
  // cancels the in-flight visit whenever a new one starts, so firing a
  // request per edit would silently drop earlier saves.
  const saveEntry = useCallback((
    metric: MetricWithEntries,
    column: DateColumn,
    key: string,
    payload: Record<string, unknown>,
    failureLabel: string,
    // The cell state this request is trying to persist. Only the fields it
    // actually writes are taken from it — a Won/Lost-only request must not
    // confirm a value the server never accepted.
    intended: CheckInData | undefined,
    fields: CellField[],
    // Captured when the user made the edit, NOT when the request goes out —
    // a draft typed during the debounce window must already own the field,
    // or the in-flight older save reverts the cell out from under it.
    revs: Record<CellField, number>
  ) => {
    markSaving(key, true)
    enqueue({
      key,
      url: entriesUrl(metric.id),
      body: { metric_entry: { recorded_at: recordedAtFor(metric, column), ...payload } },
      mergeKey: 'metric_entry',
      onSuccess: () => {
        // Advance the confirmed state field by field. Saves run one at a
        // time and in order, so the accepted request is the newest word on
        // the fields it carried — and says nothing about the others.
        const base = confirmedEntries.current[key]
        if (!intended) {
          confirmedEntries.current[key] = base && !fields.includes('value')
            ? base
            : (base ? { ...base, value: null } : undefined)
          return
        }
        const next: CheckInData = base
          ? { ...base }
          : { ...intended, value: null, on_track: null, won_the_week: null, won_the_week_manual: false }
        if (fields.includes('value')) {
          next.value = intended.value
          next.on_track = computeOnTarget(intended.value, metric.target, metric.goal_direction, metric.comparison)
        }
        if (fields.includes('won')) {
          next.won_the_week_manual = intended.won_the_week_manual
          if (intended.won_the_week_manual) next.won_the_week = intended.won_the_week
        }
        // Whenever the override is off, the server derives Won/Lost from the
        // stored value — never from whatever the cell was optimistically
        // showing, which may include an override this request didn't carry.
        if (!next.won_the_week_manual) {
          next.won_the_week = computeWon(next.value, metric.target, metric.goal_direction, metric.comparison)
        }
        confirmedEntries.current[key] = next
      },
      onError: (detail) => {
        setSaveError(`${failureLabel}${detail ? `: ${detail}` : ' could not be saved.'}`)
        // Only roll back the fields this request still owns; a field the
        // user has since re-edited belongs to that newer edit.
        const own = fields.filter(f => revs[f] === (fieldRevisions.current[key]?.[f] ?? 0))
        if (own.length > 0) revertFields(metric.id, column, key, own)
      },
      onSettled: () => markSaving(key, false),
    })
  }, [enqueue, entriesUrl, recordedAtFor, markSaving, revertFields])

  // --- Inline Target editing (task #650) -----------------------------------
  // The Target cell writes through the metric UPDATE endpoint the settings
  // modal uses, so authorization, validation and audit logging are identical.
  // It goes through the same serialized queue as the score cells: an
  // independent Inertia visit would cancel a weekly autosave mid-flight.

  const markTargetSaving = useCallback((metricId: number, delta: 1 | -1) => {
    const outstanding = Math.max(0, (targetSaveCounts.current[metricId] ?? 0) + delta)
    targetSaveCounts.current[metricId] = outstanding
    setSavingTargets(prev => {
      if (prev.has(metricId) === (outstanding > 0)) return prev
      const next = new Set(prev)
      if (outstanding > 0) next.add(metricId); else next.delete(metricId)
      return next
    })
  }, [])

  // Applies a target to the local mirror: the formatted Target cell plus the
  // win/loss coloring of every cell in that row, which is derived from the
  // target. Manual Won/Lost overrides are left alone, exactly as on the
  // server. YTD is a sum of values and is unaffected by the target.
  const applyTarget = useCallback((metricId: number, target: number | null) => {
    setOrderedMetrics(prev => prev.map(m => {
      if (m.id !== metricId) return m
      const entries: Record<string, CheckInData> = {}
      Object.entries(m.entries).forEach(([columnKey, e]) => {
        entries[columnKey] = {
          ...e,
          on_track: computeOnTarget(e.value ?? null, target, m.goal_direction, m.comparison),
          won_the_week: e.won_the_week_manual === true
            ? e.won_the_week
            : computeWon(e.value ?? null, target, m.goal_direction, m.comparison),
        }
      })
      return {
        ...m,
        target,
        formatted_target: formatTargetDisplay(target, m.comparison, m.format, m.decimal_places, m.abbreviate_large_numbers),
        entries,
      }
    }))
  }, [])

  const handleTargetSave = useCallback((metric: MetricWithEntries, raw: string) => {
    // Blank clears the target, mirroring the settings modal's handling of an
    // emptied Target field.
    const numVal = raw === '' ? null : parseFloat(raw)
    if (numVal !== null && Number.isNaN(numVal)) return
    const previous = metric.target ?? null
    if (numVal === previous) return

    // The rollback baseline is the last target the SERVER confirmed, not the
    // optimistic value a previous (possibly also failing) edit left behind.
    if (!(metric.id in confirmedTargets.current)) confirmedTargets.current[metric.id] = previous
    const rev = (targetRevisions.current[metric.id] ?? 0) + 1
    targetRevisions.current[metric.id] = rev
    setSaveError(null)
    applyTarget(metric.id, numVal)
    markTargetSaving(metric.id, 1)

    enqueue({
      key: `metric:${metric.id}:target`,
      method: 'put',
      url: `/teams/${team.id}/scoreboard/metrics/${metric.id}`,
      body: { metric: { target: numVal } },
      mergeKey: 'metric',
      onSuccess: () => {
        confirmedTargets.current[metric.id] = numVal
      },
      onError: (detail) => {
        setSaveError(`${metric.name} — target${detail ? `: ${detail}` : ' could not be saved.'}`)
        // A newer edit owns the field now; a stale failure must not stomp it.
        if (targetRevisions.current[metric.id] !== rev) return
        applyTarget(metric.id, confirmedTargets.current[metric.id] ?? null)
      },
      onSettled: () => markTargetSaving(metric.id, -1),
    })
  }, [applyTarget, markTargetSaving, enqueue, team.id])

  const flushPendingSave = useCallback((key: string) => {
    const fn = pendingSaves.current[key]
    if (!fn) return
    delete pendingSaves.current[key]
    if (debounceTimers.current[key]) {
      clearTimeout(debounceTimers.current[key])
      delete debounceTimers.current[key]
    }
    fn()
  }, [])

  const handleValueChange = useCallback((metric: MetricWithEntries, column: DateColumn, rawValue: string) => {
    const key = scoreCellKey(metric.id, column.key)
    const existing = metric.entries[column.key]
    if (!(key in confirmedEntries.current)) confirmedEntries.current[key] = existing
    // Claim the field for this keystroke immediately: anything already in
    // flight is now stale and must not roll this draft back.
    const revs = bumpRevisions(key, ['value'])
    // A fresh edit supersedes whatever the last failure was complaining
    // about; the banner is cleared here rather than on save success, so a
    // successful write to one field can't hide another field's failure.
    setSaveError(null)

    const numVal = rawValue === '' ? null : parseFloat(rawValue)
    const manual = existing?.won_the_week_manual === true
    const intended: CheckInData | undefined = (numVal === null && !existing) ? undefined : {
      id: existing?.id ?? 0,
      context: existing?.context ?? null,
      notes: existing?.notes ?? null,
      variable_target: existing?.variable_target ?? null,
      recorded_at: existing?.recorded_at ?? column.start_date,
      won_the_week_manual: existing?.won_the_week_manual,
      value: numVal,
      on_track: computeOnTarget(numVal, metric.target, metric.goal_direction, metric.comparison),
      won_the_week: manual
        ? (existing?.won_the_week ?? null)
        : computeWon(numVal, metric.target, metric.goal_direction, metric.comparison),
    }

    // Optimistic: this cell's value, its Won/Lost + on-track coloring, and
    // the row's YTD total. Nothing else moves.
    patchEntry(metric.id, column, (m, entry) => ({
      entry: intended,
      ytd: applyYtdDelta(m, column, entry?.value ?? null, numVal),
    }))

    if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key])
    pendingSaves.current[key] = () => saveEntry(
      metric,
      column,
      key,
      { value: numVal },
      `${metric.name} — ${column.label}`,
      intended,
      ['value'],
      revs
    )
    debounceTimers.current[key] = setTimeout(() => flushPendingSave(key), SAVE_DEBOUNCE_MS)
  }, [patchEntry, applyYtdDelta, saveEntry, flushPendingSave, bumpRevisions])

  // Enter in a cell: flush the pending save, then move focus to the same
  // column on the next DISPLAYED row (contents selected, ready to type
  // over). On the last row, just save — focus leaves the field.
  const handleValueEnter = useCallback((metricId: number, columnKey: string, current: HTMLInputElement) => {
    flushPendingSave(scoreCellKey(metricId, columnKey))
    const idx = displayedOrder.findIndex(m => m.id === metricId)
    const nextMetric = idx >= 0 ? displayedOrder[idx + 1] : undefined
    const nextInput = nextMetric ? valueInputRefs.current[scoreCellKey(nextMetric.id, columnKey)] : null
    if (nextInput) {
      nextInput.focus()
      // Defer select() a frame: focusing flips the cell from its formatted
      // display to the raw number input, so selecting must wait for that
      // re-render or the selection lands on the stale text.
      requestAnimationFrame(() => {
        try { nextInput.select() } catch { /* select() unsupported on some inputs */ }
      })
    } else {
      current.blur()
    }
  }, [flushPendingSave, displayedOrder])

  // Leaving a cell flushes its debounced save straight away, so a pending
  // edit isn't still sitting on an 800ms timer when the user clicks a link.
  const handleValueBlur = useCallback((metricId: number, columnKey: string) => {
    flushPendingSave(scoreCellKey(metricId, columnKey))
  }, [flushPendingSave])

  // On unmount, drop every debounce timer and shut the queue down. A save
  // that started after this page is gone would redirect back to it, undoing
  // the navigation the user just made.
  useEffect(() => () => {
    Object.values(debounceTimers.current).forEach(clearTimeout)
    debounceTimers.current = {}
    pendingSaves.current = {}
    dispose()
  }, [dispose])

  const registerValueRef = useCallback((metricId: number, columnKey: string, el: HTMLInputElement | null) => {
    valueInputRefs.current[scoreCellKey(metricId, columnKey)] = el
  }, [])

  const handleWonLostToggle = useCallback((metric: MetricWithEntries, column: DateColumn, newValue: boolean) => {
    const key = scoreCellKey(metric.id, column.key)
    const existing = metric.entries[column.key]
    const currentVal = existing?.won_the_week ?? null
    const finalVal = currentVal === newValue ? null : newValue
    if (!(key in confirmedEntries.current)) confirmedEntries.current[key] = existing
    setSaveError(null)

    // Flush any value still pending in this cell first. Both writes share
    // the same queue key, so the queue folds them into ONE request carrying
    // the value and the Won/Lost fields together — and both keep their own
    // completion callbacks.
    flushPendingSave(key)
    const revs = bumpRevisions(key, ['won'])

    // Clearing the override falls back to the auto-computed (on-target)
    // result from the cell's value — mirroring the server recompute.
    const intended: CheckInData = {
      id: existing?.id ?? 0,
      value: existing?.value ?? null,
      context: existing?.context ?? null,
      notes: existing?.notes ?? null,
      variable_target: existing?.variable_target ?? null,
      recorded_at: existing?.recorded_at ?? column.start_date,
      on_track: existing?.on_track ?? null,
      won_the_week: finalVal !== null
        ? finalVal
        : computeWon(existing?.value ?? null, metric.target, metric.goal_direction, metric.comparison),
      won_the_week_manual: finalVal !== null,
    }

    patchEntry(metric.id, column, () => ({ entry: intended }))

    saveEntry(
      metric,
      column,
      key,
      { won_the_week: finalVal, won_the_week_manual: finalVal !== null },
      `${metric.name} — ${column.label} Won/Lost`,
      intended,
      ['won'],
      revs
    )
  }, [patchEntry, flushPendingSave, saveEntry, bumpRevisions])

  const handleNotesSaved = useCallback((metricId: number, updates: EntryDetailUpdate[]) => {
    setOrderedMetrics(prev => prev.map(m => {
      if (m.id !== metricId) return m
      const entries = { ...m.entries }
      updates.forEach(u => {
        const existing = entries[u.columnKey]
        const next: CheckInData = {
          id: existing?.id ?? 0,
          value: existing?.value ?? null,
          notes: existing?.notes ?? null,
          recorded_at: existing?.recorded_at ?? u.columnKey,
          on_track: existing?.on_track ?? null,
          won_the_week: existing?.won_the_week ?? null,
          won_the_week_manual: existing?.won_the_week_manual,
          context: u.context,
          variable_target: u.variableTarget,
        }
        entries[u.columnKey] = next
        // Merge the accepted fields onto the confirmed state instead of
        // replacing it. When the cell has never been inline-edited there is
        // no confirmed record yet — and then what is on screen IS what the
        // server loaded, so that is the baseline. Fabricating a blank one
        // here would make a later failed value edit roll the cell back to
        // empty and drop its YTD contribution.
        // (Idempotent: recomputing from the same `prev` yields the same
        // result, so a double-invoked updater is harmless.)
        const cellKey = scoreCellKey(metricId, u.columnKey)
        const base = (cellKey in confirmedEntries.current)
          ? confirmedEntries.current[cellKey]
          : existing
        // The notes request writes ONLY these two fields, so it confirms
        // only these two. It says nothing about the cell's value or its
        // Won/Lost — whatever an in-flight inline edit is doing with those
        // stays that edit's business.
        confirmedEntries.current[cellKey] = base
          ? { ...base, context: u.context, variable_target: u.variableTarget }
          // No entry existed server-side; this request created one carrying
          // just the notes.
          : { ...next, value: null, on_track: null, won_the_week: null, won_the_week_manual: false }
      })
      return { ...m, entries }
    }))
  }, [])

  const handleEdit = (metric: MetricWithEntries) => {
    setEditMetric(metric)
  }

  const handleArchive = (metric: MetricWithEntries) => {
    const newState = metric.state === 'archived' ? 'active' : 'archived'
    router.put(`/teams/${team.id}/scoreboard/metrics/${metric.id}`, {
      metric: { state: newState },
    }, { preserveScroll: true })
  }

  const handleWeekLabelChange = (label: 'week_start' | 'week_ending') => {
    if (label === team.scoreboard_week_label) return
    router.put(`/teams/${team.id}/scoreboard/week_label`, {
      scoreboard_week_label: label,
    }, {
      preserveScroll: true,
      only: ['team', 'date_columns'],
    })
  }

  const handleWeekStartDayChange = (day: string) => {
    if (day === (team.scoreboard_week_start_day ?? 'monday')) return
    router.put(`/teams/${team.id}/scoreboard/week_start_day`, {
      scoreboard_week_start_day: day,
    }, {
      preserveScroll: true,
      only: ['team', 'date_columns', 'metrics'],
    })
  }

  const handleDelete = (metric: MetricWithEntries) => {
    if (!confirm(`Are you sure you want to delete "${metric.name}"?`)) return
    router.delete(`/teams/${team.id}/scoreboard/metrics/${metric.id}`, {
      preserveScroll: true,
    })
  }

  const notesMetric = notesMetricId !== null
    ? (orderedMetrics.find(m => m.id === notesMetricId) || null)
    : null

  return (
    <Layout team={{ id: team.id, name: team.name, member_count: team_members.length }} activeTab={active_tab}>
      <Head title={`${team.name} Scoreboard - Pinnacle`} />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-3xl font-semibold text-text-primary font-heading">
          Scoreboard
        </h2>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs font-semibold text-text-primary" title="Which day your team's scoreboard week starts on">
            <span>Week starts:</span>
            <select
              value={team.scoreboard_week_start_day ?? 'monday'}
              onChange={(e) => handleWeekStartDayChange(e.target.value)}
              className="border border-stone/30 rounded-lg px-2 py-1.5 text-xs font-semibold text-text-primary bg-white"
            >
              {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map(day => (
                <option key={day} value={day}>{day.charAt(0).toUpperCase() + day.slice(1)}</option>
              ))}
            </select>
          </label>
          <div className="inline-flex rounded-lg border border-stone/30 overflow-hidden" title="Week column labels">
            {([
              { key: 'week_start' as const, label: 'Week Starting' },
              { key: 'week_ending' as const, label: 'Week Ending' },
            ]).map(opt => (
              <button
                key={opt.key}
                onClick={() => handleWeekLabelChange(opt.key)}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                  (team.scoreboard_week_label ?? 'week_start') === opt.key
                    ? 'bg-[#351E1D] text-white'
                    : 'bg-white text-text-primary hover:bg-stone/10'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-lg border border-stone/30 overflow-hidden">
            {([
              { key: 'numbers' as const, label: 'Numbers' },
              { key: 'wonlost' as const, label: 'Won/Lost' },
              { key: 'both' as const, label: 'Both' },
            ]).map(opt => (
              <button
                key={opt.key}
                onClick={() => setViewMode(opt.key)}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                  viewMode === opt.key
                    ? 'bg-[#351E1D] text-white'
                    : 'bg-white text-text-primary hover:bg-stone/10'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {filters.cadence === 'weekly' && (
            <div className="inline-flex items-center whitespace-nowrap">
              <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '12px', fontWeight: 600, color: '#1B1B1B', marginRight: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Column:</span>
              <button
                type="button"
                onClick={() => setCurrentWeekVisible(v => !v)}
                aria-pressed={currentWeekVisible}
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '13px',
                  fontWeight: currentWeekVisible ? 700 : 500,
                  color: currentWeekVisible ? '#fff' : '#1B1B1B',
                  backgroundColor: currentWeekVisible ? '#1B1B1B' : '#F5F5F5',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                This Week
              </button>
            </div>
          )}
          <button
            onClick={() => setViewSettingsOpen(true)}
            className="text-sm border border-stone/30 rounded-lg px-3 py-2 text-text-primary hover:bg-stone/10 transition-colors flex items-center gap-1.5"
            title="Customize View"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            View
          </button>
          <button
            onClick={() => setCategoriesOpen(true)}
            className="text-sm border border-stone/30 rounded-lg px-3 py-2 text-text-primary hover:bg-stone/10 transition-colors flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            Categories
          </button>
          <select
            value={groupBy}
            onChange={e => setGroupBy(e.target.value)}
            className="text-sm border border-stone/30 rounded-lg px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 bg-white"
          >
            {GROUP_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            onClick={() => setAddMetricOpen(true)}
            className="bg-pinnacle-red hover:bg-pinnacle-red/90 text-white font-semibold py-2 px-4 rounded-lg transition-colors font-heading text-sm flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Metric
          </button>
        </div>
      </div>

      <div className="mb-6">
        <ScoreboardFilters teamId={team.id} filters={filters} />
      </div>

      {saveError && (
        <div
          role="alert"
          className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-[#ED1C24]/30 bg-[#ED1C24]/10 px-4 py-3 text-sm text-[#ED1C24]"
        >
          <span>{saveError}</span>
          <button
            onClick={() => setSaveError(null)}
            className="font-semibold underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <ScoreboardTable
        metrics={orderedMetrics}
        categories={categories}
        dateColumns={visibleDateColumns}
        teamId={team.id}
        groupBy={groupBy}
        viewMode={viewMode}
        savingCells={savingCells}
        savingTargets={savingTargets}
        onTargetSave={handleTargetSave}
        onValueChange={handleValueChange}
        onValueEnter={handleValueEnter}
        onValueBlur={handleValueBlur}
        registerValueRef={registerValueRef}
        onWonLostToggle={handleWonLostToggle}
        onEditNotes={(metric) => setNotesMetricId(metric.id)}
        onEdit={handleEdit}
        onArchive={handleArchive}
        onDelete={handleDelete}
        onReorder={can_reorder_metric ? handleReorder : undefined}
      />

      <EntryNotesModal
        isOpen={notesMetric !== null}
        onClose={() => setNotesMetricId(null)}
        metric={notesMetric}
        dateColumns={date_columns}
        teamId={team.id}
        enqueue={enqueue}
        onSaved={handleNotesSaved}
      />

      <AddMetricModal
        isOpen={addMetricOpen}
        onClose={() => setAddMetricOpen(false)}
        teamId={team.id}
        teamMembers={team_members}
        categories={categories}
      />

      <EditMetricModal
        isOpen={editMetric !== null}
        onClose={() => setEditMetric(null)}
        metric={editMetric}
        teamId={team.id}
        teamMembers={team_members}
        categories={categories}
      />

      <EditCategoriesModal
        isOpen={categoriesOpen}
        onClose={() => setCategoriesOpen(false)}
        categories={categories}
        teamId={team.id}
      />

      <EditViewModal
        isOpen={viewSettingsOpen}
        onClose={() => setViewSettingsOpen(false)}
        settings={viewSettings}
        onSave={setViewSettings}
      />
    </Layout>
  )
}
